import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { publicRoutes, protectedRoutes, protectedRoutesWithoutLayout, routes } from './routes';
import Layout from '../components/Layout/Layout';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAppDispatch } from '../store/hooks';
import { logout } from '../store/authSlice';

// Full-screen loading fallback component (for public routes)
const LoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <Spinner size={40} />
  </div>
);

// Content loading fallback component (for use within Layout)
const ContentLoadingFallback = () => (
  <div
    className="flex w-full items-center justify-center"
    style={{ minHeight: 'calc(100vh - 200px)' }}
  >
    <Spinner size={40} />
  </div>
);

// Recursive function to render nested route children
// Note: Child routes should NOT be wrapped in Layout since they're rendered via <Outlet />
// inside the parent route's Layout
const renderRouteChildren = (children, parentPath) => {
  if (!children || children.length === 0) return null;

  return children.map((childRoute) => {
    const ChildComponent = childRoute.element;
    // Extract the child path relative to parent (e.g., '/sales/new' -> 'new')
    let childPath = childRoute.path.replace(parentPath, '').replace(/^\//, '');

    // If child path is empty (index route), use index prop
    if (!childPath) {
      return (
        <Route
          key={childRoute.path}
          index
          element={
            <ProtectedRoute requiredDocTypes={childRoute.requiredDocTypes}>
              <Suspense fallback={<ContentLoadingFallback />}>
                <ChildComponent />
              </Suspense>
            </ProtectedRoute>
          }
        >
          {childRoute.children && renderRouteChildren(childRoute.children, childRoute.path)}
        </Route>
      );
    }

    return (
      <Route
        key={childRoute.path}
        path={childPath}
        element={
          <ProtectedRoute requiredDocTypes={childRoute.requiredDocTypes}>
            <Suspense fallback={<ContentLoadingFallback />}>
              <ChildComponent />
            </Suspense>
          </ProtectedRoute>
        }
      >
        {childRoute.children && renderRouteChildren(childRoute.children, childRoute.path)}
      </Route>
    );
  });
};

const GlobalAuthListener = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  useEffect(() => {
    const handleUnauthorized = () => {
      dispatch(logout());
      navigate('/login', { replace: true });
    };
    window.addEventListener('unauthorized-error', handleUnauthorized);
    return () => window.removeEventListener('unauthorized-error', handleUnauthorized);
  }, [navigate, dispatch]);
  
  return null;
};

const AppRouter = () => {
  return (
    <BrowserRouter>
      <GlobalAuthListener />
      <Routes>
        {/* Redirect root to dashboard (will redirect to login if not authenticated) */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Public routes (without Layout) */}
        {publicRoutes.filter(r => r.path !== '/').map((route) => {
          const Component = route.element;
          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <Component />
                </Suspense>
              }
            />
          );
        })}

        {/* Protected routes without Layout (e.g., onboarding) */}
        {protectedRoutesWithoutLayout.map((route) => {
          const Component = route.element;
          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ProtectedRoute requireOnboarding={false} requiredDocTypes={route.requiredDocTypes}>
                  <Suspense fallback={<LoadingFallback />}>
                    <Component />
                  </Suspense>
                </ProtectedRoute>
              }
            />
          );
        })}

        {/* Protected routes (with Layout) */}
        {protectedRoutes.map((route) => {
          const Component = route.element;
          // Filter out dynamic children (routes with :param) - they'll be rendered as siblings
          const staticChildren = route.children?.filter(
            child => !child.path || (!child.path.includes(':') && !child.path.includes('*'))
          );

          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ProtectedRoute requiredDocTypes={route.requiredDocTypes}>
                  <Layout>
                    <Suspense fallback={<ContentLoadingFallback />}>
                      <Component />
                    </Suspense>
                  </Layout>
                </ProtectedRoute>
              }
            >
              {staticChildren && staticChildren.length > 0 && renderRouteChildren(staticChildren, route.path)}
            </Route>
          );
        })}

        {/* Render dynamic child routes (with :param) as top-level routes */}
        {protectedRoutes
          .filter(route => route.children?.some(child => child.path && child.path.includes(':')))
          .flatMap(route =>
            route.children
              ?.filter(child => child.path && child.path.includes(':'))
              .map(childRoute => {
                const ChildComponent = childRoute.element;
                return (
                  <Route
                    key={childRoute.path}
                    path={childRoute.path}
                    element={
                      <ProtectedRoute requiredDocTypes={childRoute.requiredDocTypes}>
                        <Layout>
                          <Suspense fallback={<ContentLoadingFallback />}>
                            <ChildComponent />
                          </Suspense>
                        </Layout>
                      </ProtectedRoute>
                    }
                  >
                    {childRoute.children && renderRouteChildren(childRoute.children, childRoute.path)}
                  </Route>
                );
              }) || []
          )}

        {/* 404 route */}
        {routes
          .filter((route) => route.path === '*')
          .map((route) => {
            const Component = route.element;
            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Component />
                  </Suspense>
                }
              />
            );
          })}
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;

